import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROLE_CAPS,
  CAPABILITIES,
  type ComposeRole,
  type Capability,
  OWNER_EMAIL,
} from '../../lib/identity';
import {
  fetchPickerUsers,
  fetchRoleAssignments,
  upsertRoleAssignment,
  removeRoleAssignment,
} from '../../lib/users';
import { AccessNotice } from '../../components/AccessNotice/AccessNotice';
import { isAccessDenied } from '../../lib/dataverse';
import styles from './AdminTabs.module.css';

const ROLES: ComposeRole[] = ['user', 'auditor', 'admin'];
const CAP_LABEL: Record<Capability, string> = {
  compose: 'Compose drafts',
  review: 'Review others’ drafts',
  editLibrary: 'Edit library',
  manageUsers: 'Manage users',
};

/**
 * User access admin — ACCESS-ROLES-PATTERN.md Piece 4:
 *  1. read-only capability matrix
 *  2. add/update a person (Microsoft picker + role select)
 *  3. assigned-people list (inline role change + remove)
 */
export function UserAccessTab() {
  const queryClient = useQueryClient();

  const pickerQ = useQuery({ queryKey: ['compose.pickerUsers'], queryFn: fetchPickerUsers, staleTime: 30 * 60 * 1000 });
  const assignQ = useQuery({ queryKey: ['compose.roleAssignments'], queryFn: fetchRoleAssignments });

  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<ComposeRole>('user');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const assignedByUpn = useMemo(() => {
    const m = new Map<string, true>();
    for (const a of assignQ.data ?? []) m.set(a.gaqc_name.toLowerCase(), true);
    return m;
  }, [assignQ.data]);

  const availableUsers = useMemo(
    () => (pickerQ.data ?? []).filter((u) => !assignedByUpn.has(u.email)),
    [pickerQ.data, assignedByUpn]
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['compose.roleAssignments'] });

  const handleAdd = async () => {
    if (!selectedEmail) return;
    const picked = (pickerQ.data ?? []).find((u) => u.email === selectedEmail);
    setBusy(true);
    setError('');
    try {
      await upsertRoleAssignment(selectedEmail, selectedRole, picked?.name ?? selectedEmail);
      setSelectedEmail('');
      setSelectedRole('user');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add.');
    } finally {
      setBusy(false);
    }
  };

  const handleChangeRole = async (upn: string, displayName: string, role: ComposeRole) => {
    setError('');
    try {
      await upsertRoleAssignment(upn, role, displayName);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update role.');
    }
  };

  const handleRemove = async (id: string) => {
    setError('');
    try {
      await removeRoleAssignment(id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove.');
    }
  };

  return (
    <div className={styles.tab}>
      <header className={styles.header}>
        <h1 className={styles.title}>User access</h1>
        <p className={styles.lead}>
          Assign Compose roles. People without a row default to <strong>user</strong>.{' '}
          <code>{OWNER_EMAIL}</code> is always an admin (break-glass).
        </p>
      </header>

      {error && <div className={styles.errorBox}>{error}</div>}

      {/* 1. Capability matrix */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>What each role can do</h2>
        <div className={styles.matrixWrap}>
          <table className={styles.matrix}>
            <thead>
              <tr>
                <th>Capability</th>
                {ROLES.map((r) => (
                  <th key={r} className={styles.matrixRole}>{r}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITIES.map((cap) => (
                <tr key={cap}>
                  <td>{CAP_LABEL[cap]}</td>
                  {ROLES.map((r) => (
                    <td key={r} className={styles.matrixCell}>
                      {ROLE_CAPS[r][cap] ? <span className={styles.yes}>✓</span> : <span className={styles.no}>–</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 2. Add a person */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Add or update a person</h2>
        {pickerQ.isError &&
          (isAccessDenied(pickerQ.error) ? (
            <AccessNotice />
          ) : (
            <div className={styles.errorBox}>
              Couldn’t load the user list. {String(pickerQ.error)}
            </div>
          ))}
        <div className={styles.addRow}>
          <select
            className={styles.select}
            value={selectedEmail}
            onChange={(e) => setSelectedEmail(e.target.value)}
            disabled={pickerQ.isLoading}
          >
            <option value="">{pickerQ.isLoading ? 'Loading users…' : 'Select a person…'}</option>
            {availableUsers.map((u) => (
              <option key={u.email} value={u.email}>
                {u.name} — {u.email}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as ComposeRole)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button type="button" className={styles.primaryBtn} onClick={handleAdd} disabled={!selectedEmail || busy}>
            {busy ? 'Saving…' : 'Add'}
          </button>
        </div>
      </section>

      {/* 3. Assigned people */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Assigned people</h2>
        {assignQ.isLoading && <p className={styles.muted}>Loading…</p>}
        {assignQ.isSuccess && (assignQ.data?.length ?? 0) === 0 && (
          <p className={styles.muted}>No explicit assignments yet. Everyone defaults to <strong>user</strong>.</p>
        )}
        {assignQ.isSuccess && (assignQ.data?.length ?? 0) > 0 && (
          <table className={styles.peopleTable}>
            <thead>
              <tr><th>Name</th><th>UPN</th><th>Role</th><th></th></tr>
            </thead>
            <tbody>
              {assignQ.data!.map((a) => {
                const isOwner = a.gaqc_name.toLowerCase() === OWNER_EMAIL;
                return (
                  <tr key={a.gaqc_composeroleid ?? a.gaqc_name}>
                    <td>{a.gaqc_displayname || '—'}</td>
                    <td className={styles.mono}>{a.gaqc_name}</td>
                    <td>
                      <select
                        className={styles.inlineSelect}
                        value={a.gaqc_role}
                        disabled={isOwner}
                        title={isOwner ? 'Owner is always admin' : undefined}
                        onChange={(e) =>
                          handleChangeRole(a.gaqc_name, a.gaqc_displayname ?? a.gaqc_name, e.target.value as ComposeRole)
                        }
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </td>
                    <td className={styles.rowActions}>
                      {!isOwner && a.gaqc_composeroleid && (
                        <button type="button" className={styles.removeBtn} onClick={() => handleRemove(a.gaqc_composeroleid!)}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className={styles.footNote}>
          Removing a person reverts them to the default <strong>user</strong> role. UI gating is convenience only — destructive
          actions are also re-checked against the Dataverse security role server-side.
        </p>
      </section>
    </div>
  );
}
